// ---------------------------------------------------------------
// MOCK DATA for Fleetroot marketing site (frontend-only phase)
// Everything below will be replaced by backend / CMS data later.
// ---------------------------------------------------------------

export const IMAGES = {
  fleet:
    "https://images.unsplash.com/photo-1492168732976-2676c584c675?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  courier:
    "https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  warehouse:
    "https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  manager:
    "https://images.pexels.com/photos/38136632/pexels-photo-38136632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  dashboard:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  highway:
    "https://images.pexels.com/photos/33093563/pexels-photo-33093563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  fleetAlt:
    "https://images.pexels.com/photos/38199714/pexels-photo-38199714.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  healthcare:
    "https://images.unsplash.com/photo-1580281657527-47f249e8f4df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  parcels: "/solutions-ecommerce.jpg",
};

export const NAV_LINKS = [
  { label: "Platform", children: "PLATFORM" },
  { label: "Features", to: "/features" },
  { label: "Solutions", to: "/solutions" },
  { label: "Pricing", to: "/pricing" },
  { label: "Resources", children: "RESOURCES" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

// External links for API docs & knowledge base to be provided later (placeholders point to Contact)
export const API_DOCS_URL = "https://www.postman.com/fleetroot/fleetroot-logistics-api/request/2g9s4xt/create-a-pickup-task?sideView=agentMode";

export const RESOURCES = [
  {
    label: "Customer stories",
    to: "/case-studies",
    icon: "Award",
    text: "How pharmacies, marketplaces and 3PLs win with Fleetroot.",
  },
  {
    label: "Blog",
    to: "/blog",
    icon: "Newspaper",
    text: "Insights on last-mile delivery, fleet ops and logistics tech.",
  },
  {
    label: "API documentation",
    href: API_DOCS_URL,
    external: true,
    icon: "Code2",
    text: "REST API & webhooks to connect orders, tracking and proof.",
  },
];

export const PLATFORM = [
  { label: "Logistics Orchestration", to: "/platform/logistics-orchestration", icon: "Boxes", text: "Orders pooled, planned, dispatched and proven — on demand or on schedule." },
  { label: "Fleet Intelligence", to: "/platform/fleet-telematics", icon: "Radio", text: "Real-time visibility into vehicles, drivers, fuel, temperature and maintenance." },
];

export const SALES_EMAIL = "sales@fleetroot.com";
export const INFO_EMAIL = "info@fleetroot.com";
export const SUPPORT_EMAIL = "support@fleetroot.com";
export const BOOKING_URL = "https://calendar.app.google/zqursqSpaRVrLk4J8";
export const FREE_TRIAL_URL = "https://freetrial.fleetroot.com/get-started/create";
export const LOGIN_URL = "https://track.fleetroot.com/";

export const HERO = {
  eyebrow: "Telematics & transport operations platform",
  title: "Every vehicle. Every delivery. One platform.",
  subtitle:
    "Fleetroot helps businesses connect their vehicles, drivers, devices and transport operations to achieve real-time fleet visibility, optimized routing, efficient dispatch, and digital proof of delivery, all in one place.",
  primaryCta: { label: "Start a free trial", href: FREE_TRIAL_URL, external: true },
  secondaryCta: { label: "Request a demo", to: "/contact" },
  trust: ["14-day free trial", "No credit card", "Unlimited riders", "Live in days, not months"],
  stats: [
    { value: "20M+", label: "orders processed" },
    { value: "300+", label: "businesses onboard" },
    { value: "30%", label: "lower delivery cost*" },
  ],
  statsNote: "*Medicina Group of Pharmacies case study",
};

// h = rendered height; sizes balanced by visual footprint (wide wordmarks shorter, compact marks taller)
export const TRUSTED_LOGOS = [
  { name: "Zamil Group", src: "/logos/zamil.png", h: 36 },
  { name: "RAKEZ", src: "/logos/rakez.png", h: 42 },
  { name: "Modern Pharmacy", src: "/logos/modern-pharmacy.png", h: 42, boost: true },
  { name: "Medicina Group of Pharmacies", src: "/logos/medicina.png", h: 36 },
  { name: "Pharmalink", src: "/logos/pharmalink.png", h: 18 },
  { name: "DAMAC", src: "/logos/damac.png", h: 18 },
  { name: "Cargoline Logistics", src: "/logos/cargoline.png", h: 32 },
];
export const TRUSTED_LINE = "Logistics, pharmacy, retail and real-estate operators across the GCC.";

export const PROBLEM = {
  eyebrow: "Why Fleetroot",
  title: "One connected operation, from fleet to doorstep.",
  text:
    "Fleetroot brings telematics and delivery management together in one connected platform to manage vehicles, drivers, devices, orders, routes and deliveries.",
  pains: [
    { pain: "Missed time windows", fix: "Time-window routing with live ETAs" },
    { pain: "Reattempts and dead miles", fix: "Optimized routes and order pooling" },
    { pain: "\u201cWhere is my order?\u201d calls", fix: "Tracking links and automatic updates" },
    { pain: "Blind spots on vehicles & cargo", fix: "GPS devices and IoT sensors: location, fuel, temperature" },
    { pain: "SLA reports you can't produce", fix: "Proof on every drop, reports on demand" },
  ],
  resolve: "One live map, automated dispatch, proof on every delivery.",
};

export const HOW_IT_WORKS = {
  eyebrow: "How it works",
  title: "From order to proof in three moves",
  steps: [
    {
      n: "01",
      title: "Plan & dispatch in minutes",
      text:
        "Import orders from your ERP, e-commerce store or spreadsheet. Fleetroot pools orders, assigns riders and builds optimized routes around time windows, capacity and live traffic.",
      illustration: "route-map",
    },
    {
      n: "02",
      title: "Track every vehicle, live",
      text:
        "Trucks, vans, bikes and cars on one live map with ETAs, geofences and alerts for delays, idling or off-route driving — before your customer notices.",
      illustration: "fleet-map",
    },
    {
      n: "03",
      title: "Prove & close every delivery",
      text:
        "Riders capture signature, photo or OTP in the driver app. Customers get tracking links and updates automatically; your day closes itself.",
      illustration: "driver-app",
    },
  ],
};

export const CASE_STUDIES = {
  eyebrow: "Proof, not promises",
  title: "Real results from real operations",
  subtitle: "Outcomes reported by Fleetroot customers across pharmacy, e-commerce and 3PL operations in the GCC.",
};

export const OFFER = {
  eyebrow: "Pricing",
  title: "Simple plans. Prices on display.",
  subtitle:
    "Delivery management priced by tasks, not by seats — unlimited riders on every plan, zero hidden charges.",
  badge: "Start here",
  name: "Business Starter",
  price: "$79",
  priceSuffix: "per month, billed yearly",
  tagline: "Everything to run 800 tasks a month.",
  included: [
    "Real-time tracking",
    "Rider app",
    "Proof of delivery",
    "Unlimited riders",
    "800 tasks / mo",
    "Extra tasks $0.15",
    "Standard support",
    "14-day free trial",
  ],
  footnote: "Billed monthly or yearly — cancel anytime. Prices in USD.",
  addons: [
    {
      title: "Business Standard & Growth",
      price: "from $169 / month",
      text: "Automated dispatch, APIs, analytics, merchant portal and priority support for growing operations.",
      to: "/pricing",
    },
    {
      title: "Enterprise",
      price: "Custom",
      text: "More than 6,000 tasks a month, custom SLA and integrations — talk to sales.",
      to: "/contact",
    },
  ],
};

export const ROLES = {
  title: "Everything your delivery operation needs, connected.",
  subtitle:
    "Bring your entire operation together. Fleetroot covers the whole journey — from order import to invoice.",
  items: [
    "Route optimization",
    "Live GPS tracking",
    "Dispatch & auto-assignment",
    "Driver mobile app",
    "Proof of delivery",
    "Customer portal",
    "Fuel & maintenance",
    "Analytics & reporting",
  ],
};

export const COMPARISON = {
  title: "Basic GPS tracker vs Fleetroot",
  subtitle: "Same fleet, two answers. Only one gets the parcel to the door on time.",
  colA: "Basic GPS tracker",
  colB: "Fleetroot",
  rows: [
    { aspect: "What you see", a: "Dots on a map", b: "Routes, ETAs, tasks and proof — live" },
    { aspect: "Who plans routes", a: "Your dispatcher, by hand", b: "AI, in seconds, with traffic & time windows" },
    { aspect: "Customer experience", a: "“Where is my order?” calls", b: "Tracking link, ETA alerts, OTP handover" },
    { aspect: "Billing", a: "Manual reconciliation", b: "Auto-generated from delivered tasks" },
    { aspect: "At the end of the day", a: "You have a location history", b: "You have a closed, invoiced, reported day" },
  ],
  note: "Nothing against plain trackers: if you only need to find a stolen truck, they do the job. If you run deliveries, you need an operations platform.",
};

export const SOLUTIONS = [
  {
    slug: "healthcare",
    kind: "Healthcare logistics",
    name: "Healthcare & pharma logistics",
    tagline: "Every dose delivered on time, in range, with proof.",
    tags: [
      "Temperature compliance",
      "Chain of custody",
      "OTP handover",
      "Priority routing",
      "Time-window delivery",
      "Returns & recalls",
      "Audit-ready reports",
    ],
    image: IMAGES.healthcare,
    body:
      "Purpose-built for pharmacies, hospitals, labs and medical distributors. Fleetroot keeps cold-chain shipments within range, proves who handled every package and hits prescription delivery windows — with audit-ready records for regulators.",
  },
  {
    slug: "last-mile",
    kind: "Delivery management",
    name: "Last-mile & e-commerce",
    tagline: "From checkout to doorstep, on time.",
    tags: ["Order aggregation", "On-demand pooling", "Customer portal", "POD"],
    image: IMAGES.parcels,
    imagePosition: "center 35%",
    body:
      "Same-day, next-day and scheduled deliveries at scale. Auto-dispatch to in-house or 3PL drivers, keep customers informed and cut reattempts.",
  },
  {
    slug: "fleet",
    kind: "Fleet management",
    name: "Commercial fleets",
    tagline: "Know where every asset is and how it's driven.",
    tags: ["GPS telematics", "Driver behaviour", "Maintenance"],
    image: IMAGES.fleet,
    body:
      "Real-time telematics, fuel monitoring, maintenance schedules and driver scorecards for trucks, vans, buses and heavy equipment.",
  },
  {
    slug: "distribution",
    kind: "Distribution & 3PL",
    name: "Distribution & logistics",
    tagline: "Multi-depot, multi-client, one control tower.",
    tags: ["Route planning", "Multi-warehouse", "Merchant billing", "ERP sync"],
    image: IMAGES.warehouse,
    body:
      "Run several warehouses and merchants from one dashboard, sync with your ERP and bill each client automatically from delivered tasks.",
  },
];

export const INDUSTRIES = [
  { name: "E-commerce & retail", text: "Same-day promises kept with dynamic routing and live ETAs." },
  { name: "Food & grocery", text: "Temperature-aware, time-window routing for perishable goods." },
  { name: "Pharma & healthcare", text: "Chain-of-custody proof, OTP handover and audit-ready reports." },
  { name: "Courier & 3PL", text: "Multi-merchant billing, carrier broker and white-label tracking." },
  { name: "Field service", text: "Technician scheduling, job proof and route-aware dispatch." },
  { name: "Construction & rental", text: "Asset tracking, utilisation and maintenance for heavy equipment." },
];

export const TESTIMONIALS = [
  {
    quote:
      "We moved 40 vans from WhatsApp dispatch to Fleetroot in a week. Reattempts dropped by a third and our customers finally stopped calling to ask where their order is.",
    name: "Damien",
    company: "Blooming Box",
  },
  {
    quote:
      "The route optimization alone paid for the platform. We run the same volume with three fewer vehicles and the drivers actually like the app.",
    name: "Gatien",
    company: "Grab & Go",
  },
  {
    quote:
      "Fleetroot understood our multi-warehouse setup immediately. Billing each merchant from delivered tasks used to take two days a month — now it's automatic.",
    name: "David",
    company: "3P Logistics",
  },
];

export const FAQ = [
  {
    q: "Is live tracking included at every tier?",
    a: "Yes. Tracking is commoditized — we won't nickel-and-dime you on it. Every plan includes real-time live tracking, customer ETA notifications and public tracking links at no extra cost.",
  },
  {
    q: "What counts as a task?",
    a: "One task equals one pickup, delivery, or service stop. A pickup and delivery count as two tasks, while reattempts on the same stop do not create an additional task.",
  },
  {
    q: "Do I need new hardware?",
    a: "No. The driver app turns any smartphone into a tracker. For vehicle-level telematics (ignition, fuel, harsh driving) our partner resellers supply and install certified GPS devices across the GCC.",
  },
  {
    q: "Can Fleetroot integrate with my ERP or e-commerce stack?",
    a: "Yes. Native connectors for Oracle NetSuite, Microsoft Dynamics 365, Salesforce, Odoo, SAP and Shopify, plus a REST API and webhooks for everything else.",
  },
  {
    q: "How long does implementation take?",
    a: "Most fleets are live within days. Complex ERP integrations and multi-warehouse setups typically take two to three weeks, with a dedicated onboarding manager.",
  },
  {
    q: "Where is my data stored? Is it compliant?",
    a: "Data is stored in-region by default with custom residency available on Enterprise. We are GDPR-compliant, OWASP-aligned and pen-tested annually.",
  },
  {
    q: "Can I manage third-party carriers alongside my own fleet?",
    a: "Yes. Hand tasks off to external 3PLs, pull their tracking back into your dashboard and reconcile status events so your team sees one unified view.",
  },
  {
    q: "Does the driver app work offline?",
    a: "Not yet — offline mode is on our roadmap. Today the driver app needs a data connection to receive tasks and sync proof of delivery, and it is designed to work reliably on low-bandwidth mobile networks. Ask us about timelines if offline support is critical for your operation.",
  },
];

export const RESOURCE_LINKS = [
  {
    title: "Fleetroot, compared.",
    text: "The right questions before choosing a delivery platform — and why we built Fleetroot the way we did.",
    to: "/features",
  },
  {
    title: "Who for, what for.",
    text: "Whatever you move — parcels, pallets or people — Fleetroot fits your operation.",
    to: "/solutions",
  },
];

export const FINAL_CTA = {
  title: "Every delivery, planned, tracked and proven.",
  text: "Start free in minutes — no card, unlimited riders, cancel anytime. Or tell us what you move and we'll reply within 24h with a tailored walkthrough.",
  cta: { label: "Start a free trial", href: FREE_TRIAL_URL, external: true },
  secondary: { label: "Request a demo", to: "/contact" },
  email: SALES_EMAIL,
  whatsapp: "https://wa.me/971525234328?text=Hi%20Fleetroot%2C%20I%27d%20like%20a%20demo!",
};

export const FOOTER = {
  blurb: "Fleet management & last-mile delivery platform.",
  columns: [
    {
      heading: "Product",
      links: [
        { label: "Logistics Orchestration", to: "/platform/logistics-orchestration" },
        { label: "Fleet Intelligence", to: "/platform/fleet-telematics" },
        { label: "Features", to: "/features" },
        { label: "Solutions", to: "/solutions" },
        { label: "Pricing", to: "/pricing" },
        { label: "Integrations", to: "/features#integrations" },
        { label: "Log in", href: LOGIN_URL },
      ],
    },
    {
      heading: "Company",
      links: [
        { label: "About", to: "/about" },
        { label: "Customer stories", to: "/case-studies" },
        { label: "Contact", to: "/contact" },
        { label: "Careers", to: "/about" },
        { label: "Partners", to: "/partners" },
      ],
    },
    {
      heading: "Resources",
      links: [
        { label: "Blog", to: "/blog" },
        { label: "API documentation", href: API_DOCS_URL },
      ],
    },
    {
      heading: "Legal",
      links: [
        { label: "Privacy policy", to: "/legal/privacy-policy" },
        { label: "Terms of service", to: "/legal/terms-of-service" },
      ],
    },
  ],
};

// ---------------- Features page ----------------
export const FEATURE_GROUPS = [
  {
    id: "tracking",
    eyebrow: "Fleet tracking",
    title: "See every vehicle, live",
    text: "GPS telematics with second-by-second positions, sensor data, trip history, geofences and instant alerts.",
    bullets: ["Live map with ETAs", "Trip replay & history", "Geofence & idle alerts", "Fuel & engine diagnostics"],
    illustration: "fleet-map",
  },
  {
    id: "routing",
    eyebrow: "Route optimization",
    title: "Plan a day of deliveries in seconds",
    text: "AI builds multi-stop routes that respect capacity, time windows, driver shifts and live traffic.",
    bullets: ["Multi-stop optimization", "Time windows & capacities", "Dynamic re-routing", "Zone & territory planning"],
    illustration: "route-map",
  },
  {
    id: "driver",
    eyebrow: "Driver app",
    title: "An app drivers actually use",
    text: "Turn-by-turn navigation, task lists, live status updates and one-tap proof of delivery on iOS and Android.",
    bullets: ["Stop sequence & navigation", "Signature, photo & OTP proof", "In-app chat", "Cash on delivery"],
    illustration: "driver-app",
  },
];

export const FEATURE_GRID = [
  { icon: "Route", title: "Route Optimization", text: "AI multi-stop routes on real-time traffic and capacity." },
  { icon: "MapPin", title: "Live Tracking", text: "Real-time driver location and ETA updates." },
  { icon: "LayoutGrid", title: "Tasks Dispatching", text: "Assign, reassign and auto-dispatch from one board." },
  { icon: "Smartphone", title: "Driver App", text: "Execute routes and capture proof on any phone." },
  { icon: "ClipboardCheck", title: "Proof of Delivery", text: "Digital signatures, photos and OTPs synced instantly." },
  { icon: "Users", title: "Customer Portal", text: "Branded tracking links and self-serve rescheduling." },
  { icon: "MessageSquare", title: "Messaging Automation", text: "WhatsApp, SMS and email at every milestone." },
  { icon: "Receipt", title: "Delivery Billing", text: "Invoice merchants automatically from delivered tasks." },
  { icon: "Fuel", title: "Fuel & Maintenance", text: "Consumption, service schedules and cost per km." },
  { icon: "Gauge", title: "Driver Behaviour", text: "Harsh braking, speeding and idling scorecards." },
  { icon: "BarChart3", title: "Analytics Dashboards", text: "On-time rate, cost per drop and utilisation." },
  { icon: "ShieldCheck", title: "Enterprise-grade", text: "SSO, roles, audit logs and 99.9% uptime." },
];

export const INTEGRATIONS = [
  "Oracle NetSuite",
  "Microsoft Dynamics 365",
  "Salesforce",
  "Odoo",
  "SAP",
  "Shopify",
  "WooCommerce",
  "Zoho",
];

// ---------------- Pricing page (from fleetroot.com/pricing) ----------------
export const PRICING_PLANS = [
  {
    name: "Business Starter",
    monthly: 99,
    yearly: 79,
    deliveries: "800",
    extraTask: "0.15",
    text: "For small teams getting their first deliveries under control.",
  },
  {
    name: "Business Standard",
    monthly: 200,
    yearly: 169,
    deliveries: "2,000",
    extraTask: "0.12",
    text: "Automated dispatch and APIs for growing operations.",
    highlight: true,
  },
  {
    name: "Business Growth",
    monthly: 500,
    yearly: 429,
    deliveries: "6,000",
    extraTask: "0.10",
    text: "Analytics, merchant portal and premium support at scale.",
  },
];

// value: true = included, false = not included, "addon" = available as paid add-on
export const PRICING_FEATURES = [
  { name: "Real-time tracking", starter: true, standard: true, growth: true },
  { name: "Rider app", starter: true, standard: true, growth: true },
  { name: "Proof of pick-up and delivery", starter: true, standard: true, growth: true },
  { name: "Automated dispatch", starter: false, standard: true, growth: true },
  { name: "Standard webhook / API", starter: false, standard: true, growth: true },
  { name: "Advanced webhook engine", starter: false, standard: false, growth: true },
  { name: "Delivery analytics", starter: false, standard: false, growth: true },
  { name: "Route optimization", starter: false, standard: "addon", growth: "addon" },
  { name: "Route planner (up to 500 stops)", starter: false, standard: false, growth: "addon" },
  { name: "E-commerce connectors", starter: false, standard: "addon", growth: "addon" },
  { name: "Merchant portal", starter: false, standard: false, growth: "addon" },
  { name: "Order pooling engine", starter: false, standard: false, growth: "addon" },
  { name: "Branded customer app & dashboard", starter: false, standard: false, growth: "addon" },
  { name: "Shipping label & QR scanning", starter: false, standard: false, growth: "addon" },
  { name: "Advanced SLA engine", starter: false, standard: false, growth: "addon" },
  { name: "IoT / sensor data module", starter: false, standard: "addon", growth: "addon" },
  { name: "Support", starter: "Standard", standard: "Priority", growth: "Premium" },
];

export const PRICING_FAQ = [
  {
    q: "Do I need to add my credit / debit card for the trial?",
    a: "No. We don't need any card details to create an account. You can add unlimited riders, assets and tasks within the trial period before taking a commercial decision.",
  },
  {
    q: "How does the 14-day free trial work?",
    a: "The trial lets you try the Fleetroot subscription that suits your business. During the trial you can add any number of assets, riders, routes and tasks to see how Fleetroot helps you scale.",
  },
  {
    q: "How does your pricing work?",
    a: "Once you've test-run Fleetroot and decide to purchase, choose a monthly or annual plan according to your convenience. Annual plans are billed upfront at the discounted rate shown.",
  },
  {
    q: "What counts as a task?",
    a: "A stop is a task, whether it is a pick-up or a delivery. Tasks beyond your plan's monthly allowance are billed per extra task at your plan's rate.",
  },
  {
    q: "Where can I find details on each feature?",
    a: "Our knowledge base covers subscription functionality, tutorial videos and resources — and our support team is always available to help.",
  },
];

// ---------------- About page ----------------
export const ABOUT = {
  eyebrow: "About Fleetroot",
  title: "Built by operators, for operators.",
  text:
    "Founded in 2021, Fleetroot started with a simple goal: make fleet and delivery operations easier to manage. Today, we help businesses connect their vehicles, drivers and deliveries through one connected platform.",
  story: [
    "Fleetroot aimed to be more than a logistics solution; it aimed to be a force driving connection, orchestration and automation — with tenacity, creativity and trust as the values that define us.",
  ],
  mission: {
    title: "Our mission",
    text: "Revolutionize last-mile delivery with advanced tech and tailored solutions — boosting efficiency, elevating customer satisfaction and driving sustainable growth for businesses.",
  },
  vision: {
    title: "Our vision",
    text: "Seamless last-mile delivery as a key to business success: data-driven, customer-centric, cost-effective and eco-friendly deliveries for every company.",
  },
  values: [
    { title: "Humble", text: "We foster humility, promoting a culture of listening and learning." },
    { title: "Adaptable", text: "We tailor our solutions to integrate seamlessly into diverse business workflows." },
    { title: "Positive", text: "Our positive outlook fuels innovation and lasting partnerships." },
    { title: "Passionate", text: "Passion drives our commitment to tech-driven excellence in last-mile delivery." },
    { title: "Integrity", text: "Ethical, transparent decisions that build trust in every partnership." },
    { title: "Empowered", text: "User-centric tools that empower teams and clients to succeed." },
    { title: "Responsible", text: "Reducing environmental impact and promoting sustainability in delivery." },
  ],
  milestones: [
    { year: "Mar 2021", title: "Fleetroot is born", text: "Incorporated in the MENA region by mobility and technology specialists, with one vision: simplify how businesses move people, goods and services." },
    { year: "2021", title: "First product. First customers.", text: "Delivery management platform launches — dispatch, driver operations and proof of delivery in one place. Pre-seed raised to accelerate growth." },
    { year: "2022", title: "From delivery to connected operations", text: "Automated route planning and IoT-powered fleet management go live, taking the platform beyond deliveries." },
    { year: "2023", title: "Backed to scale", text: "Joins Flat6Labs; supported by MBRIF, TASMU Accelerator and Microsoft for Startups. Fleetroot Inc. established in the USA." },
    { year: "2024", title: "Building across the region", text: "Scales across the GCC, powering delivery, fleet and transportation operations for a growing base of enterprise customers." },
    { year: "2025", title: "One platform. Multiple mobility needs.", text: "Hundreds of businesses across multiple markets manage deliveries, fleets and transportation on Fleetroot." },
    { year: "2026", title: "The next chapter", text: "Scaling the platform, expanding the ecosystem and redefining connected mobility across the region and beyond." },
  ],
  stats: [
    { value: "10+", label: "countries represented" },
    { value: "20M+", label: "orders processed" },
    { value: "300+", label: "customers served" },
    { value: "$5M+", label: "savings in logistics cost" },
  ],
  backedBy: [{ name: "Flat6Labs", src: "/backers/flat6labs.png", href: "https://www.flat6labs.com/" }],
  supportedBy: [
    { name: "Mohammed Bin Rashid Innovation Fund", src: "/backers/mbrif.png", href: "https://mbrif.ae/" },
    { name: "TASMU Accelerator", src: "/backers/tasmu.png", href: "https://accelerator.tasmu.gov.qa/" },
    { name: "Microsoft for Startups", src: "/backers/microsoft-for-startups.png", href: "https://www.microsoft.com/en-us/startups" },
  ],
  teamIntro: {
    title: "Our outstanding customer-facing team",
    text: "A diverse blend of skills and talents, committed to excellence in every endeavor and working together to exceed expectations.",
  },
  team: [
    { name: "Moidu Chandanam", role: "Co-founder · CEO", img: "/team/moidu-chandanam.jpg" },
    { name: "Motilal Keshavdas", role: "Co-founder · CTO", img: "/team/motilal-keshavdas.jpg" },
    { name: "Javokhir Vapaev", role: "Co-founder · COO", img: "/team/javokhir-vapaev.jpg" },
    { name: "Nabeel Abdul", role: "Operations Head", img: "/team/nabeel-abdul.jpg" },
    { name: "Dattatray Kanaki", role: "Product Head", img: "/team/dattatray-kanaki.jpg" },
    { name: "Riju Winston", role: "Customer Success", img: "/team/riju-winston.jpg" },
    { name: "Mary Ann", role: "Business Executive", img: "/team/mary-ann.jpg" },
    { name: "Jaya Ajay", role: "Finance Executive", img: "/team/jaya-ajay.jpg" },
    { name: "Akmal Sattarov", role: "Business Executive", img: "/team/akmal-sattarov.jpg" },
  ],
  teamPhotos: ["/team/team-1.jpg", "/team/team-2.jpg"],
  offices: [
    {
      country: "USA",
      entity: "Fleetroot Inc",
      address: "2055 Limestone Rd STE 200C, Wilmington, DE 19808",
      phone: "(+1) 646 513 4211",
      phoneHref: "tel:+16465134211",
    },
    {
      country: "UAE",
      entity: "Fleetroot FZCO",
      address: "Office #9WC 523, Scality, DAFZA, Dubai, UAE",
      phone: "+971 52 52 34 328",
      phoneHref: "tel:+971525234328",
    },
    {
      country: "UAE",
      entity: "Fleetroot / Wexoz",
      address: "Office #407, Sulthan Investment Group Building, Airport Road, Port Saeed, Dubai, UAE",
      phone: "+971 52 52 34 328",
      phoneHref: "tel:+971525234328",
    },
    {
      country: "Qatar",
      entity: "Fleetroot SaaS LLC",
      address: "Floor No. 6, Zone C, Ooredoo HQ2, Old Airport Road, Doha, Qatar",
    },
  ],
};

// ---------------- Contact page ----------------
export const CONTACT = {
  eyebrow: "Contact",
  title: "Let's talk about your fleet.",
  text: "Tell us what you move and how many vehicles. A product specialist replies within 24 hours with a tailored walkthrough.",
  email: SALES_EMAIL,
  phone: "+971 52 52 34 328",
  phoneHref: "tel:+971525234328",
  generalPhone: "+971 55 200 5851",
  generalPhoneHref: "tel:+971552005851",
  address: "Dubai, United Arab Emirates",
  mapUrl: "https://www.google.com/maps?ll=25.098859,55.177313&z=15&t=m&hl=en&cid=2050938126724019710",
  fleetSizes: ["1–10 vehicles", "11–50 vehicles", "51–200 vehicles", "200+ vehicles"],
};

export const SOCIALS = [
  { name: "Facebook", icon: "Facebook", href: "https://www.facebook.com/fleetroot" },
  { name: "LinkedIn", icon: "Linkedin", href: "https://www.linkedin.com/company/30948197/" },
  { name: "YouTube", icon: "Youtube", href: "https://www.youtube.com/channel/UCjW0ow7iEsu_r6sdLQnHe3w/featured" },
  { name: "Instagram", icon: "Instagram", href: "https://instagram.com/fleetroot.inc" },
];
